import { useState, useRef } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface SelectOption {
  value: string;
  label: string;
}

interface InlineSelectFieldProps {
  value: string | null;
  options: SelectOption[];
  onSave: (value: string) => Promise<void>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  testId?: string;
  selectedLabel?: string;
}

type SaveState = "idle" | "saving" | "success" | "error";

export function InlineSelectField({
  value,
  options,
  onSave,
  placeholder = "Select...",
  className,
  disabled = false,
  label,
  testId,
  selectedLabel,
}: InlineSelectFieldProps) {
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  const handleValueChange = async (newValue: string) => {
    if (newValue === value) {
      return;
    }

    setSaveState("saving");

    try {
      await onSave(newValue);
      setSaveState("success");
      
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveState("idle");
      }, 2000);
    } catch {
      setSaveState("error");
      
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveState("idle");
      }, 3000);
    }
  };

  const SaveIndicator = () => {
    if (saveState === "saving") {
      return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    }
    if (saveState === "success") {
      return <Check className="h-4 w-4 text-green-600 dark:text-green-400" />;
    }
    if (saveState === "error") {
      return <X className="h-4 w-4 text-destructive" />;
    }
    return null;
  };

  const currentLabel = value
    ? options.find((opt) => opt.value === value)?.label ?? selectedLabel
    : null;

  return (
    <div className="space-y-1">
      {label && (
        <label className="text-sm font-medium text-muted-foreground">
          {label}
        </label>
      )}
      <div className="relative flex items-center gap-2">
        <Select
          value={value || undefined}
          onValueChange={handleValueChange}
          disabled={disabled || saveState === "saving"}
        >
          <SelectTrigger
            className={cn("flex-1", className)}
            data-testid={testId}
          >
            <SelectValue placeholder={placeholder}>
              {currentLabel || placeholder}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                data-testid={`${testId}-option-${option.value}`}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="w-4 flex items-center justify-center">
          <SaveIndicator />
        </div>
      </div>
    </div>
  );
}
